import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HelpLayout from '../../../components/help/HelpLayout';
import { 
  BriefcaseIcon,
  HeartIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const OpportunitiesHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'User Guide', href: '/help/getting-started' },
    { name: 'Opportunities & Jobs' }
  ];

  const opportunityTypes = [
    {
      title: 'Job Opportunities',
      description: 'Full-time and part-time employment positions',
      icon: BriefcaseIcon,
      features: ['Permanent positions', 'Contract work', 'Entry-level to senior roles', 'Various industries']
    },
    {
      title: 'Apprenticeships',
      description: 'Learn while you earn with structured training programs',
      icon: CheckCircleIcon,
      features: ['Hands-on training', 'Mentorship programs', 'Certification pathways', 'Career progression']
    },
    {
      title: 'Internships',
      description: 'Gain valuable work experience and industry exposure',
      icon: ClockIcon,
      features: ['Short-term placements', 'Skills development', 'Networking opportunities', 'Career exploration']
    }
  ];

  const searchTips = [
    {
      title: 'Use Keywords Effectively',
      description: 'Search by job title, company name, skills, or industry keywords to find relevant opportunities.'
    },
    {
      title: 'Filter by Location',
      description: 'Use location filters to find opportunities in your preferred area or within commuting distance.'
    },
    {
      title: 'Set Date Preferences',
      description: 'Filter by application deadlines or start dates to find opportunities that match your timeline.'
    },
    {
      title: 'Browse by Category',
      description: 'Explore opportunities by industry, experience level, or opportunity type to discover new possibilities.'
    }
  ];

  return (
    <HelpLayout title="Opportunities & Jobs" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        {/* Introduction */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Opportunities & Jobs Guide
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '24px'
            }}>
              GMSP3 Career Guidance connects you with job opportunities, apprenticeships, and internships
              across Gauteng Province. This guide will help you navigate the opportunities marketplace and 
              maximize your chances of finding the perfect career opportunity.
            </p>

            {/* Quick Start */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--primary-yellow-light)',
              borderRadius: '8px',
              border: '1px solid var(--primary-yellow)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🚀 Quick Start
              </h3>
              <p style={{ color: 'var(--text-gray)', margin: 0, lineHeight: 1.6 }}>
                Navigate to <strong>Opportunities</strong> from the main menu to start exploring available 
                positions. Use the search and filter tools to find opportunities that match your skills and interests.
              </p>
            </div>
          </div>
        </div>

        {/* Types of Opportunities */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              💼 Types of Opportunities
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {opportunityTypes.map((type, index) => {
                const Icon = type.icon;
                return (
                  <div key={index} style={{
                    padding: '20px',
                    border: '1px solid var(--border-gray)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--white)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <Icon style={{
                        width: '24px',
                        height: '24px',
                        color: 'var(--primary-yellow)'
                      }} />
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'var(--primary-black)',
                        margin: 0
                      }}>
                        {type.title}
                      </h4>
                    </div>
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.6,
                      marginBottom: '16px'
                    }}>
                      {type.description}
                    </p>
                    <ul style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.6,
                      paddingLeft: '16px',
                      margin: 0
                    }}>
                      {type.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Search and Browse */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              🔍 Search and Browse Opportunities
            </h3>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '12px'
              }}>
                Search Features
              </h4>
              <ul style={{
                color: 'var(--text-gray)',
                lineHeight: 1.6,
                paddingLeft: '20px',
                marginBottom: '16px'
              }}>
                <li><strong>Keyword Search:</strong> Search by job title, company name, or skills</li>
                <li><strong>Location Filter:</strong> Find opportunities in specific areas</li>
                <li><strong>Opportunity Type:</strong> Filter by jobs, apprenticeships, or internships</li>
                <li><strong>Application Deadline:</strong> Sort by closing dates</li>
                <li><strong>Start Date:</strong> Filter by when positions begin</li>
              </ul>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '12px'
              }}>
                Search Tips
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                {searchTips.map((tip, index) => (
                  <div key={index} style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      {tip.title}
                    </h5>
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      lineHeight: 1.5,
                      margin: 0
                    }}>
                      {tip.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Application Process */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              📝 Application Process
            </h3>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                Step-by-Step Application Guide
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  {
                    step: '1',
                    title: 'Review Opportunity Details',
                    description: 'Read the job description, requirements, and application deadline carefully.'
                  },
                  {
                    step: '2',
                    title: 'Check Your Eligibility',
                    description: 'Ensure you meet the minimum requirements and qualifications listed.'
                  },
                  {
                    step: '3',
                    title: 'Complete Your Profile',
                    description: 'Make sure your profile is 100% complete with all required information.'
                  },
                  {
                    step: '4',
                    title: 'Submit Application',
                    description: 'Click "Apply Now" and follow the application process.'
                  },
                  {
                    step: '5',
                    title: 'Track Your Application',
                    description: 'Monitor your application status in your dashboard.'
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    gap: '16px',
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: 'var(--primary-yellow)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: 'var(--white)',
                      flexShrink: 0
                    }}>
                      {item.step}
                    </div>
                    <div>
                      <h5 style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'var(--primary-black)',
                        marginBottom: '4px'
                      }}>
                        {item.title}
                      </h5>
                      <p style={{
                        fontSize: '14px',
                        color: 'var(--text-gray)',
                        lineHeight: 1.5,
                        margin: 0
                      }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Wishlist Feature */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <HeartIcon style={{ width: '24px', height: '24px', color: 'var(--primary-yellow)' }} />
              Wishlist Feature
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '16px'
            }}>
              Save interesting opportunities to your wishlist for easy access later. This helps you:
            </p>
            <ul style={{
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              paddingLeft: '20px',
              marginBottom: '16px'
            }}>
              <li>Keep track of opportunities you're considering</li>
              <li>Compare different positions side by side</li>
              <li>Get reminders about application deadlines</li>
              <li>Quickly access saved opportunities from your dashboard</li>
            </ul>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-gray)',
              fontStyle: 'italic'
            }}>
              <strong>Tip:</strong> Click the heart icon on any opportunity card to add it to your wishlist.
            </p>
          </div>
        </div>

        {/* Next Steps */}
        <div className="card">
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              🎯 Next Steps
            </h3>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <Link to="/opportunities" className="btn btn-primary">
                Browse Opportunities
              </Link>
              <Link to="/help/career-guidance" className="btn btn-outline">
                Career Guidance
              </Link>
              <Link to="/help/profile" className="btn btn-outline">
                Complete Profile
              </Link>
            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default OpportunitiesHelp;
